import { utils } from '../../tools/Utils.js';

class AutocompleteCore {
    constructor(host, options) {
        this._initialAttributes = new Map();
        this._searchSeq = 0;
        this._lastTerm = "";
        this._items = [];
        this._activeIndex = -1;
        this._isOpen = false;
        this._destroyed = false;
        this._openInit = null;
        this._onKeydown = (event) => this._handleKeydown(event);
        this._onInput = () => this._handleInput();
        this._onDocumentMouseDown = (event) => this._handleDocumentMouseDown(event);
        this._onListClick = (event) => this._handleListClick(event);
        this._onListMouseDown = (event) => event.preventDefault();
        this._onToggleClick = () => { void this.search("   "); };
        this._onViewportChange = () => {
            if (this._isOpen)
                this._reposition();
        };
        if (!(host instanceof HTMLInputElement) && !(host instanceof HTMLTextAreaElement)) {
            throw new Error("[AutocompleteCore] host must be an input or textarea");
        }
        if (typeof options?.provider !== "function") {
            throw new Error("[AutocompleteCore] options.provider must be a function");
        }
        if (typeof options?.onSelect !== "function") {
            throw new Error("[AutocompleteCore] options.onSelect must be a function");
        }
        this._host = host;
        this._opts = this._normalizeOptions(options);
        this._initialCornerAll = host.classList.contains("ui-corner-all");
        this._initialCornerTop = host.classList.contains("ui-corner-top");
        const attributes = [
            "role",
            "aria-autocomplete",
            "aria-expanded",
            "aria-controls",
            "aria-activedescendant",
        ];
        attributes.forEach(name => this._initialAttributes.set(name, host.getAttribute(name)));
        const menuHost = this._resolveAppendTo(this._opts.appendTo ?? this._opts.target);
        this._menuWrap = document.createElement("div");
        this._menuWrap.className = "im-combo-menu im-autocomplete-menu ui-front";
        this._menuWrap.style.position = "absolute";
        this._menuWrap.style.display = "none";
        this._menuWrap.style.zIndex = "9999";
        this._menuList = document.createElement("ul");
        this._menuList.className = "ui-autocomplete ui-front ui-menu ui-widget ui-widget-content";
        this._menuList.id = `ui-id-${AutocompleteCore._idSeed++}`;
        this._menuList.setAttribute("role", "listbox");
        this._menuWrap.appendChild(this._menuList);
        menuHost.appendChild(this._menuWrap);
        host.setAttribute("role", "combobox");
        host.setAttribute("aria-autocomplete", "list");
        host.setAttribute("aria-expanded", "false");
        host.setAttribute("aria-controls", this._menuList.id);
        host.addEventListener("keydown", this._onKeydown);
        host.addEventListener("input", this._onInput);
        document.addEventListener("mousedown", this._onDocumentMouseDown);
        this._menuList.addEventListener("click", this._onListClick);
        this._menuList.addEventListener("mousedown", this._onListMouseDown);
        window.addEventListener("resize", this._onViewportChange);
        window.addEventListener("scroll", this._onViewportChange, true);
        const next = host.nextElementSibling;
        this._toggle = next?.classList.contains("im-combo-box-toggle") ? next : null;
        this._toggle?.addEventListener("click", this._onToggleClick);
    }
    get isOpen() {
        return this._isOpen;
    }
    get items() {
        return this._items;
    }
    get activeIndex() {
        return this._activeIndex;
    }
    async search(term) {
        if (this._destroyed)
            return;
        this._lastTerm = term;
        const seq = ++this._searchSeq;
        try {
            const items = await this._opts.provider(term);
            if (this._destroyed || seq !== this._searchSeq || term !== this._lastTerm)
                return;
            this._items = Array.isArray(items) ? items : [];
            this._render();
        }
        catch (error) {
            if (this._destroyed || seq !== this._searchSeq)
                return;
            console.error("AutocompleteCore: search error", error);
            this._items = [];
            this._setHidden();
        }
    }
    open() {
        if (this._destroyed || this._isOpen || !this._selectableIndices().length)
            return;
        this._isOpen = true;
        this._menuWrap.style.display = "block";
        this._reposition();
        if (this._opts.menuclass)
            document.body.classList.add(this._opts.menuclass);
        this._host.setAttribute("aria-expanded", "true");
        this._host.classList.remove("ui-corner-all");
        this._host.classList.add("ui-corner-top");
    }
    hide() {
        if (this._destroyed)
            return;
        this._invalidateSearch();
        this._setHidden();
    }
    destroy() {
        if (this._destroyed)
            return;
        this._destroyed = true;
        this._invalidateSearch();
        this._setHidden();
        this._host.removeEventListener("keydown", this._onKeydown);
        this._host.removeEventListener("input", this._onInput);
        document.removeEventListener("mousedown", this._onDocumentMouseDown);
        this._menuList.removeEventListener("click", this._onListClick);
        this._menuList.removeEventListener("mousedown", this._onListMouseDown);
        window.removeEventListener("resize", this._onViewportChange);
        window.removeEventListener("scroll", this._onViewportChange, true);
        this._toggle?.removeEventListener("click", this._onToggleClick);
        this._menuWrap.remove();
        for (const [name, value] of this._initialAttributes) {
            if (value === null)
                this._host.removeAttribute(name);
            else
                this._host.setAttribute(name, value);
        }
        this._host.classList.toggle("ui-corner-all", this._initialCornerAll);
        this._host.classList.toggle("ui-corner-top", this._initialCornerTop);
        this._items = [];
        this._activeIndex = -1;
    }
    _normalizeOptions(options) {
        const minlength = Number.isFinite(options.minlength)
            ? Math.max(0, Math.trunc(options.minlength))
            : 2;
        return {
            provider: options.provider,
            minlength,
            htmlmode: !!options.htmlmode,
            autoHtml: options.autoHtml ?? true,
            sanitizeHtml: options.sanitizeHtml ?? (html => html),
            menuclass: options.menuclass ?? "",
            appendTo: options.appendTo,
            target: options.target,
            onSelect: options.onSelect,
            onItemAction: options.onItemAction,
            resolveTerm: options.resolveTerm ?? (rawValue => rawValue),
        };
    }
    _handleInput() {
        const term = this._opts.resolveTerm(this._host.value ?? "");
        if (term.length < this._opts.minlength) {
            this._invalidateSearch();
            this._setHidden();
            return;
        }
        void this.search(term);
    }
    _handleKeydown(event) {
        if (event.key === "Tab" && this._isOpen && this._activeIndex >= 0) {
            event.preventDefault();
            this._selectByIndex(this._activeIndex);
            return;
        }
        if (!this._isOpen) {
            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const term = this._opts.resolveTerm(this._host.value ?? "");
                this._openInit = event.key === "ArrowUp" ? "last" : "first";
                void this.search(term.length < this._opts.minlength ? "   " : term);
            }
            return;
        }
        switch (event.key) {
            case "ArrowDown":
                event.preventDefault();
                this._moveActive(1);
                break;
            case "ArrowUp":
                event.preventDefault();
                this._moveActive(-1);
                break;
            case "Home":
                event.preventDefault();
                this._activateBoundary("first");
                break;
            case "End":
                event.preventDefault();
                this._activateBoundary("last");
                break;
            case "PageDown":
                event.preventDefault();
                this._moveActive(5);
                break;
            case "PageUp":
                event.preventDefault();
                this._moveActive(-5);
                break;
            case "Enter":
                if (this._activeIndex >= 0) {
                    event.preventDefault();
                    this._selectByIndex(this._activeIndex);
                }
                break;
            case "Escape":
                event.preventDefault();
                this.hide();
                break;
        }
    }
    _handleDocumentMouseDown(event) {
        if (!this._isOpen || !(event.target instanceof Node))
            return;
        if (this._menuWrap.contains(event.target) || this._host.contains(event.target))
            return;
        this.hide();
    }
    _handleListClick(event) {
        if (!(event.target instanceof Element))
            return;
        const action = event.target.closest("[data-item-action]");
        if (action && this._menuList.contains(action)) {
            const wrapper = action.closest(".ui-menu-item-wrapper");
            const index = Number(wrapper?.dataset.index);
            const item = this._items[index];
            if (item && this._opts.onItemAction) {
                event.preventDefault();
                event.stopPropagation();
                this._opts.onItemAction(action.dataset.itemAction ?? "", item, index);
            }
            return;
        }
        const wrapper = event.target.closest(".ui-menu-item-wrapper");
        if (!wrapper || !this._menuList.contains(wrapper))
            return;
        this._selectByIndex(Number(wrapper.dataset.index));
    }
    _render() {
        this._menuList.replaceChildren();
        const selectable = this._selectableIndices();
        if (!selectable.length) {
            this._activeIndex = -1;
            this._setHidden();
            return;
        }
        this._items.forEach((item, index) => {
            const li = document.createElement("li");
            li.className = "ui-menu-item";
            if (item.kind === "separator") {
                li.setAttribute("role", "separator");
                this._menuList.appendChild(li);
                return;
            }
            li.setAttribute("role", "presentation");
            const wrapper = document.createElement("div");
            wrapper.className = "ui-menu-item-wrapper im-combo-item";
            // 2026-08-14: служебные пункты («использовать как текст», «создать запись»)
            // оформляются отдельно от найденных записей справочника
            if (item.kind && item.kind !== "item") {
                wrapper.classList.add("im-autocomplete-service");
            }
            wrapper.id = `${this._menuList.id}-item-${index}`;
            wrapper.dataset.index = String(index);
            wrapper.setAttribute("role", "option");
            wrapper.setAttribute("aria-selected", "false");
            if (this._opts.htmlmode || (this._opts.autoHtml && this._isHtmlValue(item.value))) {
                wrapper.innerHTML = this._opts.sanitizeHtml(item.value);
            }
            else {
                wrapper.textContent = item.value;
            }
            item.actions?.forEach(action => wrapper.appendChild(this._renderAction(action)));
            li.appendChild(wrapper);
            this._menuList.appendChild(li);
        });
        this._activeIndex = this._openInit === "last"
            ? selectable[selectable.length - 1]
            : selectable[0];
        this._openInit = null;
        this._syncActive();
        this.open();
    }
    _renderAction(action) {
        const button = document.createElement("button");
        button.type = "button";
        button.dataset.itemAction = action.id;
        button.title = action.title;
        button.setAttribute("aria-label", action.title);
        // 2026-08-14: путь до спрайта резолвится utils.createSvg по текущему проекту
        // (ImSys.siteBaseKey) — хардкодить /src/vs/... нельзя, виджет живёт и в других проектах
        button.innerHTML = utils.createSvg(action.icon);
        // Иконка декоративная: доступное имя даёт aria-label кнопки
        button.firstElementChild?.setAttribute("aria-hidden", "true");
        return button;
    }
    _setHidden() {
        this._isOpen = false;
        this._menuWrap.style.display = "none";
        if (this._opts.menuclass)
            document.body.classList.remove(this._opts.menuclass);
        this._menuList.querySelectorAll(".ui-menu-item-wrapper.ui-state-active")
            .forEach(element => {
            element.classList.remove("ui-state-active");
            element.setAttribute("aria-selected", "false");
        });
        this._host.setAttribute("aria-expanded", "false");
        this._host.removeAttribute("aria-activedescendant");
        this._host.classList.remove("ui-corner-top");
        this._host.classList.add("ui-corner-all");
        this._activeIndex = -1;
        this._openInit = null;
    }
    _moveActive(step) {
        const selectable = this._selectableIndices();
        if (!selectable.length)
            return;
        const current = selectable.indexOf(this._activeIndex);
        const base = current < 0 ? 0 : current;
        const next = ((base + step) % selectable.length + selectable.length) % selectable.length;
        this._activeIndex = selectable[next];
        this._syncActive();
    }
    _activateBoundary(boundary) {
        const selectable = this._selectableIndices();
        if (!selectable.length)
            return;
        this._activeIndex = boundary === "first" ? selectable[0] : selectable[selectable.length - 1];
        this._syncActive();
    }
    _syncActive() {
        this._menuList.querySelectorAll(".ui-menu-item-wrapper.ui-state-active")
            .forEach(element => {
            element.classList.remove("ui-state-active");
            element.setAttribute("aria-selected", "false");
        });
        if (this._activeIndex < 0) {
            this._host.removeAttribute("aria-activedescendant");
            return;
        }
        const active = document.getElementById(`${this._menuList.id}-item-${this._activeIndex}`);
        if (!active || !this._menuList.contains(active))
            return;
        active.classList.add("ui-state-active");
        active.setAttribute("aria-selected", "true");
        active.scrollIntoView({ block: "nearest" });
        this._host.setAttribute("aria-activedescendant", active.id);
    }
    _selectByIndex(index) {
        const item = this._items[index];
        if (!item || item.kind === "separator")
            return;
        try {
            this._opts.onSelect(item, index);
        }
        finally {
            this.hide();
        }
    }
    _selectableIndices() {
        const indices = [];
        this._items.forEach((item, index) => {
            if (item.kind !== "separator")
                indices.push(index);
        });
        return indices;
    }
    _invalidateSearch() {
        this._searchSeq++;
        this._lastTerm = "";
    }
    _reposition() {
        const rect = this._host.getBoundingClientRect();
        const top = rect.bottom + (window.pageYOffset || document.documentElement.scrollTop);
        const left = rect.left + (window.pageXOffset || document.documentElement.scrollLeft);
        this._menuWrap.style.top = `${top}px`;
        this._menuWrap.style.left = `${left}px`;
        this._menuWrap.style.minWidth = `${rect.width}px`;
    }
    _resolveAppendTo(target) {
        if (target instanceof HTMLElement)
            return target;
        if (typeof target === "string")
            return document.querySelector(target) ?? document.body;
        const view = this._host.closest(".im-view");
        return view?.querySelector(".h0:last-of-type") ?? document.body;
    }
    _isHtmlValue(value) {
        return /<\/?[a-z][\s\S]*>/i.test(value);
    }
}
AutocompleteCore._idSeed = 1;

export { AutocompleteCore };
